export const STANDARD_IMAGE = {
  width: 1920,
  height: 1080,
  aspectRatio: '16:9',
  maxSizeMb: 10,
  formats: ['jpg', 'jpeg', 'png', 'webp'] as const,
};

export const STANDARD_VIDEO = {
  width: 1920,
  height: 1080,
  aspectRatio: '16:9',
  maxSizeMb: 50,
  maxDurationSeconds: 60,
  formats: ['mp4', 'mov'] as const,
};

export const PARTICIPANT_MEDIA_LIMIT = {
  maxImages: 1,
  maxVideos: 1,
};

export type AllowedMediaType = 'image' | 'video' | 'both';

export interface ContestMediaRules {
  allowed_media_types?: string;
  allowed_formats?: unknown;
  max_images?: number;
  max_videos?: number;
  max_image_size_mb?: number;
  max_video_size_mb?: number;
  max_video_duration_seconds?: number;
}

export function getDefaultAllowedFormats(mediaType: AllowedMediaType): string[] {
  if (mediaType === 'image') return [...STANDARD_IMAGE.formats];
  if (mediaType === 'video') return [...STANDARD_VIDEO.formats];
  return [...STANDARD_IMAGE.formats, ...STANDARD_VIDEO.formats];
}

export function getMediaDefaultsForType(mediaType: AllowedMediaType) {
  if (mediaType === 'image') {
    return {
      max_images: PARTICIPANT_MEDIA_LIMIT.maxImages,
      max_videos: 0,
      allowed_formats: getDefaultAllowedFormats('image'),
      max_image_size_mb: STANDARD_IMAGE.maxSizeMb,
      max_video_size_mb: STANDARD_VIDEO.maxSizeMb,
      max_video_duration_seconds: STANDARD_VIDEO.maxDurationSeconds,
    };
  }
  if (mediaType === 'video') {
    return {
      max_images: 0,
      max_videos: PARTICIPANT_MEDIA_LIMIT.maxVideos,
      allowed_formats: getDefaultAllowedFormats('video'),
      max_image_size_mb: STANDARD_IMAGE.maxSizeMb,
      max_video_size_mb: STANDARD_VIDEO.maxSizeMb,
      max_video_duration_seconds: STANDARD_VIDEO.maxDurationSeconds,
    };
  }
  return {
    max_images: PARTICIPANT_MEDIA_LIMIT.maxImages,
    max_videos: PARTICIPANT_MEDIA_LIMIT.maxVideos,
    allowed_formats: getDefaultAllowedFormats('both'),
    max_image_size_mb: STANDARD_IMAGE.maxSizeMb,
    max_video_size_mb: STANDARD_VIDEO.maxSizeMb,
    max_video_duration_seconds: STANDARD_VIDEO.maxDurationSeconds,
  };
}

export function parseAllowedFormats(
  formats: unknown,
  mediaType: AllowedMediaType = 'both'
): string[] {
  let parsed: string[] = [];

  if (Array.isArray(formats)) {
    parsed = formats
      .map((format) => String(format).toLowerCase())
      .filter(Boolean);
  } else if (typeof formats === 'string') {
    try {
      const jsonParsed = JSON.parse(formats);
      if (Array.isArray(jsonParsed)) {
        parsed = jsonParsed.map((format) => String(format).toLowerCase()).filter(Boolean);
      }
    } catch {
      parsed = formats.split(',').map((format) => format.trim().toLowerCase()).filter(Boolean);
    }
  }

  if (parsed.length === 0) {
    return getDefaultAllowedFormats(mediaType);
  }

  return parsed;
}

export function extensionFromUrl(url: string): string | null {
  const lower = url.toLowerCase();
  const match = lower.match(/\.([a-z0-9]+)(?:\?|#|$)/);
  if (match) return match[1];

  try {
    const parsed = new URL(url);
    const key = parsed.searchParams.get('key');
    if (key) {
      const keyMatch = key.toLowerCase().match(/\.([a-z0-9]+)$/);
      if (keyMatch) return keyMatch[1];
    }
  } catch {
    // ignore invalid URLs
  }

  return null;
}

export function inferMediaKindFromUrl(url: string): 'image' | 'video' | 'unknown' {
  const extension = extensionFromUrl(url);
  if (extension) {
    if ((STANDARD_VIDEO.formats as readonly string[]).includes(extension)) return 'video';
    if ((STANDARD_IMAGE.formats as readonly string[]).includes(extension) || extension === 'jpeg') {
      return 'image';
    }
  }

  const lower = url.toLowerCase();
  if (/\.(mp4|mov|webm|avi)(\?|#|$)/.test(lower)) return 'video';
  if (/\.(jpg|jpeg|png|webp|gif)(\?|#|$)/.test(lower)) return 'image';
  if (lower.includes('type=video') || lower.includes('/video')) return 'video';
  return 'unknown';
}

export function validateAllowedFormatsSelection(
  formats: string[],
  mediaType: AllowedMediaType
): { valid: boolean; error?: string } {
  const normalized = formats.map((format) => format.toLowerCase());
  if (normalized.length === 0) {
    return { valid: false, error: 'Select at least one allowed format' };
  }

  const imageFormats = STANDARD_IMAGE.formats as readonly string[];
  const videoFormats = STANDARD_VIDEO.formats as readonly string[];

  if (mediaType === 'image' || mediaType === 'both') {
    const hasImageFormat = normalized.some((format) => imageFormats.includes(format));
    if (!hasImageFormat) {
      return { valid: false, error: 'Select at least one image format' };
    }
  }

  if (mediaType === 'video' || mediaType === 'both') {
    const hasVideoFormat = normalized.some((format) => videoFormats.includes(format));
    if (!hasVideoFormat) {
      return { valid: false, error: 'Select at least one video format' };
    }
  }

  return { valid: true };
}

export function validateMediaUrlForContest(
  url: string,
  contest: ContestMediaRules
): { valid: boolean; error?: string } {
  const trimmed = url.trim();
  if (!trimmed) return { valid: true };

  const mediaType = (contest.allowed_media_types || 'both') as AllowedMediaType;
  const allowedFormats = parseAllowedFormats(contest.allowed_formats, mediaType);
  const kind = inferMediaKindFromUrl(trimmed);
  const extension = extensionFromUrl(trimmed);

  if (mediaType === 'image' && kind === 'video') {
    return { valid: false, error: 'This contest only accepts images (1 per entry).' };
  }
  if (mediaType === 'video' && kind === 'image') {
    return { valid: false, error: 'This contest only accepts videos (1 per entry).' };
  }

  if (extension) {
    if (!allowedFormats.includes(extension)) {
      return {
        valid: false,
        error: `Format not allowed. Allowed: ${allowedFormats.map((f) => f.toUpperCase()).join(', ')}`,
      };
    }
  } else if (kind === 'unknown') {
    return {
      valid: false,
      error: 'Could not detect file format. Use a URL ending in a supported extension.',
    };
  }

  return { valid: true };
}

function fileExtension(name: string): string {
  const parts = name.split('.');
  if (parts.length < 2) return '';
  return parts.pop()!.toLowerCase();
}

function normalizeFormatExt(ext: string): string {
  return ext === 'jpeg' ? 'jpg' : ext;
}

function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  const ext = fileExtension(file.name);
  return (STANDARD_IMAGE.formats as readonly string[]).includes(ext) || ext === 'jpeg';
}

function isVideoFile(file: File): boolean {
  if (file.type.startsWith('video/')) return true;
  const ext = fileExtension(file.name);
  return (STANDARD_VIDEO.formats as readonly string[]).includes(ext);
}

export function getMediaFileAcceptValue(contest: ContestMediaRules): string {
  const mediaType = (contest.allowed_media_types || 'both') as AllowedMediaType;
  const formats = parseAllowedFormats(contest.allowed_formats, mediaType);
  const tokens = new Set<string>();

  const addImage = (format: string) => {
    tokens.add(`.${format === 'jpeg' ? 'jpg' : format}`);
    if (format === 'jpg' || format === 'jpeg') tokens.add('image/jpeg');
    else if (format === 'png') tokens.add('image/png');
    else if (format === 'webp') tokens.add('image/webp');
  };

  const addVideo = (format: string) => {
    tokens.add(`.${format}`);
    tokens.add(format === 'mov' ? 'video/quicktime' : `video/${format}`);
  };

  if (mediaType === 'image' || mediaType === 'both') {
    formats.forEach((format) => {
      if ((STANDARD_IMAGE.formats as readonly string[]).includes(format)) addImage(format);
    });
  }
  if (mediaType === 'video' || mediaType === 'both') {
    formats.forEach((format) => {
      if ((STANDARD_VIDEO.formats as readonly string[]).includes(format)) addVideo(format);
    });
  }

  return Array.from(tokens).join(',') || 'image/*,video/*';
}

export function validateMediaFile(
  file: File,
  contest: ContestMediaRules
): { valid: boolean; error?: string } {
  const mediaType = (contest.allowed_media_types || 'both') as AllowedMediaType;
  const allowedFormats = parseAllowedFormats(contest.allowed_formats, mediaType);
  const ext = normalizeFormatExt(fileExtension(file.name));
  const image = isImageFile(file);
  const video = isVideoFile(file);

  if (mediaType === 'image' && video && !image) {
    return { valid: false, error: 'Este concurso solo acepta imágenes (1 por participación).' };
  }
  if (mediaType === 'video' && image && !video) {
    return { valid: false, error: 'Este concurso solo acepta videos (1 por participación).' };
  }
  if (!image && !video) {
    return { valid: false, error: 'Selecciona un archivo de imagen o video válido.' };
  }

  const formatMatches = ext && (
    allowedFormats.includes(ext) ||
    (ext === 'jpg' && allowedFormats.includes('jpeg')) ||
    (ext === 'jpeg' && allowedFormats.includes('jpg'))
  );
  if (!formatMatches) {
    return {
      valid: false,
      error: `Formato no permitido. Permitidos: ${allowedFormats.map((f) => f.toUpperCase()).join(', ')}`,
    };
  }

  const sizeMb = file.size / (1024 * 1024);
  if (video) {
    const maxVideoMb = contest.max_video_size_mb || STANDARD_VIDEO.maxSizeMb;
    if (sizeMb > maxVideoMb) {
      return { valid: false, error: `El video supera el máximo de ${maxVideoMb}MB.` };
    }
  } else {
    const maxImageMb = contest.max_image_size_mb || STANDARD_IMAGE.maxSizeMb;
    if (sizeMb > maxImageMb) {
      return { valid: false, error: `La imagen supera el máximo de ${maxImageMb}MB.` };
    }
  }

  return { valid: true };
}

export function formatMediaRulesSummary(contest: ContestMediaRules): string {
  const mediaType = contest.allowed_media_types || 'both';
  const formats = parseAllowedFormats(contest.allowed_formats, mediaType as AllowedMediaType)
    .map((format) => format.toUpperCase())
    .join(', ');

  if (mediaType === 'image') {
    return `1 image · ${STANDARD_IMAGE.width}×${STANDARD_IMAGE.height}px · max ${contest.max_image_size_mb || STANDARD_IMAGE.maxSizeMb}MB · ${formats}`;
  }
  if (mediaType === 'video') {
    return `1 video · ${STANDARD_VIDEO.width}×${STANDARD_VIDEO.height}px · max ${contest.max_video_size_mb || STANDARD_VIDEO.maxSizeMb}MB · ${contest.max_video_duration_seconds || STANDARD_VIDEO.maxDurationSeconds}s · ${formats}`;
  }
  return `1 image or 1 video · ${STANDARD_IMAGE.width}×${STANDARD_IMAGE.height}px · ${formats}`;
}

export function getParticipantMediaLabel(contest: ContestMediaRules): string {
  const mediaType = contest.allowed_media_types || 'both';
  if (mediaType === 'video') return 'Video de tu mascota';
  if (mediaType === 'image') return 'Foto de tu mascota';
  return 'Foto o video de tu mascota';
}
